import { useEffect, useState } from 'react';
import {
  changeManageUpdatePreferencesV2,
  fetchSettingsManageUpdates
} from '@actions/auth';
import useCountries from '@api/hooks/useCountries';
import useParentContact from '@api/hooks/parentContact/useParentContact';
import {
  PARENT_CONTACT_ANALYTICS_SOURCES,
  trackAddParentContactClicked
} from '@stores/parent-contact/parentContactAnalytics';
import useSubscribed from '@hooks/useSubscribed';
import { getStore } from 'src/redux';
import ParentContactVerificationDrawer from './ParentContactVerificationDrawer';
import useShouldShowParentProgressFloater from './useShouldShowParentProgressFloater';
import { isParentProgressManageUpdateBlock } from './parentProgressFloaterUtils';

interface ParentProgressFloaterProps {
  goalUID?: string;
  openRequestId?: number;
}

const ParentProgressFloater = ({
  goalUID,
  openRequestId = 0
}: ParentProgressFloaterProps) => {
  const shouldShowFloater = useShouldShowParentProgressFloater(goalUID);
  const countries = useCountries();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const userHasSubscription = useSubscribed(goalUID);

  const { refreshStatus, sendOtp, verifyOtp } = useParentContact();

  useEffect(() => {
    if (shouldShowFloater) getStore().dispatch(fetchSettingsManageUpdates());
  }, [shouldShowFloater]);

  useEffect(() => {
    if (!openRequestId || !shouldShowFloater) return;
    trackAddParentContactClicked({
      source: PARENT_CONTACT_ANALYTICS_SOURCES.HOME_BANNER,
      userHasSubscription
    });
    setIsDrawerOpen(true);
    // Only the click request should open the drawer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequestId]);

  if (!shouldShowFloater) {
    return null;
  }

  const handleVerified = async () => {
    await refreshStatus();
    const store = getStore();
    const { manageUpdates } = store.getState()?.auth ?? {};
    const parentProgressBlockType =
      manageUpdates && !Array.isArray(manageUpdates)
        ? Object.keys(manageUpdates).find((blockType) =>
            isParentProgressManageUpdateBlock(
              blockType,
              manageUpdates[blockType]
            )
          )
        : undefined;

    if (parentProgressBlockType) {
      store.dispatch(
        changeManageUpdatePreferencesV2(
          parentProgressBlockType,
          'whatsapp',
          true
        )
      );
    }
    setIsDrawerOpen(false);
  };

  return (
    <>
      <ParentContactVerificationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onVerified={handleVerified}
        sendOtp={sendOtp}
        verifyOtp={verifyOtp}
        countries={countries}
        analyticsSource={PARENT_CONTACT_ANALYTICS_SOURCES.HOME_BANNER}
        userHasSubscription={userHasSubscription}
      />
    </>
  );
};

export default ParentProgressFloater;
